import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, Clock, Users, Star, CheckCircle, Gift, 
  Camera, Video, Square, Eye, Target, Trophy,
  Calendar, Check, UserCheck, BookOpen
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { VideoRecorder, getVideoExtension } from '@/utils/videoRecorder';
import { isNativePlatform, capturePhoto, photoToFile } from '@/utils/nativeCamera';

interface SurveyQuestion {
  id: string;
  text: string;
  type: 'multiple_choice' | 'scale' | 'text' | 'yes_no';
  options?: string[];
  required: boolean;
}

interface Quest {
  id: string;
  worker_id: string;
  template_id: string;
  status: string;
  expires_at: string | null;
  cluster_quest_id: string | null;
  response_data: Record<string, any> | null;
  quest_templates: {
    title: string;
    description: string;
    quest_type: string;
    xp_reward: number;
    content: Record<string, any>;
  } | null;
  cluster_quests: {
    title: string;
    target_value: number;
    current_value: number;
  } | null;
}

interface SurveyTemplate {
  id: string;
  title: string;
  description: string;
  questions: SurveyQuestion[];
}

interface TeamMember {
  id: string;
  display_name: string;
  avatar_url?: string;
}

export default function QuestDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [quest, setQuest] = useState<Quest | null>(null);
  const [surveyTemplate, setSurveyTemplate] = useState<SurveyTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [responses, setResponses] = useState<Record<string, any>>({});
  const [showSuccess, setShowSuccess] = useState(false);
  const [earnedXp, setEarnedXp] = useState(0);

  // Peer observation state
  const [checkedItems, setCheckedItems] = useState<Record<string, boolean>>({});
  const [observationNotes, setObservationNotes] = useState('');
  const [observationPhoto, setObservationPhoto] = useState<string | null>(null);
  const [selectedObservee, setSelectedObservee] = useState<string>('');
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);

  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [videoStream, setVideoStream] = useState<MediaStream | null>(null);
  const videoRecorderRef = useRef<VideoRecorder | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const previewRef = useRef<HTMLVideoElement | null>(null);

  // Challenge state
  const [dailyCompletions, setDailyCompletions] = useState<string[]>([]);
  const [todayCompleted, setTodayCompleted] = useState(false);

  // Commitment state
  const [commitmentAgreed, setCommitmentAgreed] = useState(false);

  // Cluster challenge state
  const [contributionCount, setContributionCount] = useState(0);

  useEffect(() => {
    if (id) loadQuest();
  }, [id]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isRecording) {
      interval = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isRecording]);

  useEffect(() => {
    return () => {
      if (videoUrl) URL.revokeObjectURL(videoUrl);
      if (videoStream) {
        videoStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [videoUrl, videoStream]);

  const loadQuest = async () => {
    try {
      const { data: questData, error } = await supabase
        .from('worker_quests')
        .select(`
          *,
          quest_templates (*),
          cluster_quests (*)
        `)
        .eq('id', id)
        .single();

      if (error) throw error;
      
      const questTemplates = questData.quest_templates as any;
      // Only redirect to learning module if module_id exists
      // Otherwise render inline learning content
      if (questTemplates?.quest_type === 'learning' && questTemplates?.content?.module_id) {
        navigate(`/learning/${questTemplates.content.module_id}`);
        return;
      }
      
      setQuest(questData as unknown as Quest);

      const existingData = questData.response_data as Record<string, any> || {};
      if (existingData.dailyCompletions) {
        setDailyCompletions(existingData.dailyCompletions);
        const today = new Date().toISOString().split('T')[0];
        setTodayCompleted(existingData.dailyCompletions.includes(today));
      }
      if (existingData.observedWorkerId) {
        setSelectedObservee(existingData.observedWorkerId);
      }

      if (questTemplates?.quest_type === 'survey') {
        const { data: survey } = await supabase
          .from('survey_templates')
          .select('*')
          .eq('quest_template_id', questData.template_id)
          .single();
        
        if (survey) {
          const mappedQuestions = (survey.questions as any[]).map((q: any, idx: number) => ({
            id: q.id || `q_${idx}`,
            text: q.question_text || q.text || q.question || '',
            type: q.question_type || q.type || 'text',
            options: q.options || [],
            required: q.required !== false,
          }));
          setSurveyTemplate({
            ...survey,
            questions: mappedQuestions,
          } as unknown as SurveyTemplate);
        } else if (questTemplates?.content && typeof questTemplates.content === 'object' && 'questions' in questTemplates.content) {
          const contentQuestions = (questTemplates.content as { questions: any[] }).questions;
          const mappedQuestions = contentQuestions.map((q: any, idx: number) => ({
            id: q.id || `q_${idx}`,
            text: q.question_text || q.text || q.question || '',
            type: q.question_type || q.type || 'scale',
            options: q.options || [],
            required: q.required !== false,
          }));
          setSurveyTemplate({
            id: questData.template_id,
            title: questTemplates.title,
            description: questTemplates.description,
            questions: mappedQuestions,
          });
        }
      }

      if (questTemplates?.quest_type === 'observation') {
        const { data: workerProfile } = await supabase
          .from('worker_profiles')
          .select('company_id, team, cluster_id')
          .eq('id', questData.worker_id)
          .single();

        if (workerProfile) {
          const { data: members } = await supabase
            .from('worker_profiles')
            .select('id, display_name')
            .eq('company_id', workerProfile.company_id)
            .neq('id', questData.worker_id)
            .limit(50);
          
          setTeamMembers((members || []).map(m => ({ ...m, avatar_url: undefined })));
        }
      }

      if (questData.status === 'pending') {
        await supabase
          .from('worker_quests')
          .update({ status: 'in_progress', started_at: new Date().toISOString() })
          .eq('id', id);
      }
    } catch (error) {
      console.error('Error loading quest:', error);
      toast.error('Gagal memuat quest');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!quest) return;

    const questType = quest.quest_templates?.quest_type;

    if (surveyTemplate) {
      const requiredQuestions = surveyTemplate.questions.filter(q => q.required);
      const missingAnswers = requiredQuestions.filter(q => !responses[q.id]);
      if (missingAnswers.length > 0) {
        toast.error('Mohon jawab semua pertanyaan yang wajib');
        return;
      }
    }

    if (questType === 'observation') {
      if (!selectedObservee) {
        toast.error('Mohon pilih rekan yang diobservasi');
        return;
      }
      const content = quest.quest_templates?.content?.peerObservation;
      if (content?.photoRequired && !observationPhoto) {
        toast.error('Mohon ambil foto observasi');
        return;
      }
      if (Object.keys(checkedItems).length === 0) {
        toast.error('Mohon centang minimal satu item observasi');
        return;
      }
    }

    if (questType === 'recording' && !recordedBlob) {
      toast.error('Mohon rekam video keselamatan');
      return;
    }

    if (questType === 'commitment' && !commitmentAgreed) {
      toast.error('Mohon setujui komitmen');
      return;
    }

    setSubmitting(true);
    try {
      let responseData: Record<string, any> = responses;

      if (questType === 'observation') {
        responseData = {
          observedWorkerId: selectedObservee,
          checkedItems,
          notes: observationNotes,
          photoUrl: observationPhoto,
          observedAt: new Date().toISOString(),
        };
      } else if (questType === 'recording') {
        let videoStorageUrl = null;
        if (recordedBlob) {
          const ext = getVideoExtension(recordedBlob.type);
          const fileName = `videos/${quest.worker_id}/${quest.id}_${Date.now()}.${ext}`;
          const { data: uploadData, error: uploadError } = await supabase.storage
            .from('quest-uploads')
            .upload(fileName, recordedBlob, { contentType: recordedBlob.type });
          
          if (!uploadError && uploadData) {
            const { data: publicUrlData } = supabase.storage
              .from('quest-uploads')
              .getPublicUrl(fileName);
            videoStorageUrl = publicUrlData.publicUrl;
          }
        }
        
        responseData = {
          recordingDuration: recordingTime,
          videoUrl: videoStorageUrl,
          recordedAt: new Date().toISOString(),
        };
      } else if (questType === 'team_challenge') {
        responseData = {
          contributionCount,
          completed: true,
        };
      } else if (questType === 'challenge') {
        responseData = {
          dailyCompletions,
          currentStreak: dailyCompletions.length,
          completed: true,
        };
      } else if (questType === 'commitment') {
        responseData = {
          commitmentAgreed: true,
          agreedAt: new Date().toISOString(),
        };
      }

      const { data, error } = await supabase.functions.invoke('complete-quest', {
        body: {
          questId: quest.id,
          responseData,
        },
      });

      if (error) throw error;

      setEarnedXp(data.totalXp || quest.quest_templates?.xp_reward || 0);
      setShowSuccess(true);
    } catch (error) {
      console.error('Error completing quest:', error);
      toast.error('Gagal menyelesaikan quest');
    } finally {
      setSubmitting(false);
    }
  };

  const updateResponse = (questionId: string, value: any) => {
    setResponses(prev => ({ ...prev, [questionId]: value }));
  };

  const toggleCheckItem = (itemId: string) => {
    setCheckedItems(prev => ({ ...prev, [itemId]: !prev[itemId] }));
  };

  const handleTakePhoto = async () => {
    try {
      // Use native camera on mobile devices
      if (isNativePlatform()) {
        const photo = await capturePhoto('prompt');
        if (!photo) return; // User cancelled
        
        const file = await photoToFile(photo);
        if (!file) {
          toast.error('Gagal memproses foto');
          return;
        }
        
        const fileName = `observations/${quest?.worker_id}/${quest?.id}_${Date.now()}.jpg`;
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('quest-uploads')
          .upload(fileName, file, { contentType: file.type });
        
        if (!uploadError && uploadData) {
          const { data: publicUrlData } = supabase.storage
            .from('quest-uploads')
            .getPublicUrl(fileName);
          setObservationPhoto(publicUrlData.publicUrl);
          toast.success('Foto berhasil diambil');
        } else if (photo.webPath) {
          setObservationPhoto(photo.webPath);
        }
      } else {
        // Web fallback with file input
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        
        input.onchange = async (e) => {
          const file = (e.target as HTMLInputElement).files?.[0];
          if (file) {
            const fileName = `observations/${quest?.worker_id}/${quest?.id}_${Date.now()}.jpg`;
            const { data: uploadData, error: uploadError } = await supabase.storage
              .from('quest-uploads')
              .upload(fileName, file, { contentType: file.type });
            
            if (!uploadError && uploadData) {
              const { data: publicUrlData } = supabase.storage
                .from('quest-uploads')
                .getPublicUrl(fileName);
              setObservationPhoto(publicUrlData.publicUrl);
              toast.success('Foto berhasil diambil');
            } else {
              const reader = new FileReader();
              reader.onload = () => setObservationPhoto(reader.result as string);
              reader.readAsDataURL(file);
            }
          }
        };
        
        input.click();
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('Error taking photo:', error);
      if (errorMessage === 'PERMISSION_DENIED') {
        toast.error('Izin kamera/galeri ditolak. Aktifkan di pengaturan perangkat.');
      } else {
        toast.error('Gagal mengambil foto');
      }
    }
  };

  const startRecording = async () => {
    try {
      videoRecorderRef.current = new VideoRecorder();
      
      const stream = await videoRecorderRef.current.start();
      setVideoStream(stream);
      
      if (previewRef.current) {
        previewRef.current.srcObject = stream;
        previewRef.current.muted = true;
        previewRef.current.playsInline = true;
        try {
          await previewRef.current.play();
        } catch (playError) {
          console.warn('Auto-play prevented:', playError);
        }
      }
      
      setIsRecording(true);
      setRecordingTime(0);
      setRecordedBlob(null);
      if (videoUrl) {
        URL.revokeObjectURL(videoUrl);
        setVideoUrl(null);
      }
    } catch (error: any) {
      console.error('Error starting recording:', error);
      toast.error(error.message || 'Gagal memulai rekaman');
    }
  };

  const stopRecording = async () => {
    try {
      if (videoRecorderRef.current) {
        const blob = await videoRecorderRef.current.stop();
        
        if (blob.size === 0) {
          throw new Error('Rekaman kosong');
        }
        
        setRecordedBlob(blob);
        const url = URL.createObjectURL(blob);
        setVideoUrl(url);
        setVideoStream(null);
        
        if (previewRef.current) {
          previewRef.current.srcObject = null;
        }
        
        toast.success('Rekaman video disimpan!');
      }
    } catch (error: any) {
      console.error('Error stopping recording:', error);
      toast.error(error.message || 'Gagal menyimpan rekaman');
    } finally {
      setIsRecording(false);
    }
  };

  const toggleRecording = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const handleDailyCheckIn = async () => {
    const today = new Date().toISOString().split('T')[0];
    if (!dailyCompletions.includes(today)) {
      const newCompletions = [...dailyCompletions, today];
      setDailyCompletions(newCompletions);
      setTodayCompleted(true);
      
      if (quest) {
        await supabase
          .from('worker_quests')
          .update({ 
            response_data: { 
              dailyCompletions: newCompletions,
              currentStreak: newCompletions.length 
            } 
          })
          .eq('id', quest.id);
      }
      
      toast.success('Check-in hari ini berhasil!');
    }
  };

  const handleLogContribution = async () => {
    const newCount = contributionCount + 1;
    setContributionCount(newCount);
    
    if (quest?.cluster_quest_id) {
      await supabase
        .from('cluster_quests')
        .update({ 
          current_value: (quest.cluster_quests?.current_value || 0) + 1 
        })
        .eq('id', quest.cluster_quest_id);
    }
    
    toast.success(`+1 Kontribusi! Total: ${newCount}`);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getLast7Days = () => {
    const days = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      days.push(date.toISOString().split('T')[0]);
    }
    return days;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full"
        />
      </div>
    );
  }

  if (!quest) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Quest tidak ditemukan</p>
      </div>
    );
  }

  // Success screen
  if (showSuccess) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="text-center max-w-sm"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring" }}
            className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center"
          >
            <Gift className="h-10 w-10 text-white" />
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-2xl font-bold mb-2"
          >
            Quest Selesai!
          </motion.h1>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="flex items-center justify-center gap-2 text-3xl font-bold text-amber-500 mb-8"
          >
            <Star className="h-8 w-8" />
            <span>+{earnedXp} XP</span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
          >
            <Button onClick={() => navigate('/quests')} className="w-full h-12 rounded-xl">
              Kembali ke Quest
            </Button>
          </motion.div>
        </motion.div>
      </div>
    );
  }

  const questType = quest.quest_templates?.quest_type;
  const content = quest.quest_templates?.content;

  const renderQuestContent = () => {
    // Survey Quest
    if (questType === 'survey' && surveyTemplate) {
      return (
        <div className="space-y-4">
          {surveyTemplate.questions.map((question, index) => (
            <motion.div 
              key={question.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="p-4 rounded-2xl bg-card/50"
            >
              <div className="flex items-start gap-3 mb-4">
                <span className="w-6 h-6 rounded-lg bg-primary/10 text-primary text-xs font-medium flex items-center justify-center shrink-0">
                  {index + 1}
                </span>
                <div>
                  <p className="font-medium">{question.text}</p>
                  {question.required && (
                    <span className="text-xs text-destructive">* Wajib</span>
                  )}
                </div>
              </div>

              {question.type === 'multiple_choice' && question.options && (
                <RadioGroup
                  value={responses[question.id] || ''}
                  onValueChange={(value) => updateResponse(question.id, value)}
                  className="space-y-2"
                >
                  {question.options.map((option, optIndex) => (
                    <div key={optIndex} className="flex items-center space-x-3 p-3 rounded-xl hover:bg-muted/50 transition-colors">
                      <RadioGroupItem value={option} id={`${question.id}-${optIndex}`} />
                      <Label htmlFor={`${question.id}-${optIndex}`} className="cursor-pointer text-sm">{option}</Label>
                    </div>
                  ))}
                </RadioGroup>
              )}

              {question.type === 'yes_no' && (
                <RadioGroup
                  value={responses[question.id] || ''}
                  onValueChange={(value) => updateResponse(question.id, value)}
                  className="flex gap-3"
                >
                  <div className="flex-1 flex items-center space-x-3 p-3 rounded-xl hover:bg-muted/50 transition-colors">
                    <RadioGroupItem value="yes" id={`${question.id}-yes`} />
                    <Label htmlFor={`${question.id}-yes`} className="cursor-pointer">Ya</Label>
                  </div>
                  <div className="flex-1 flex items-center space-x-3 p-3 rounded-xl hover:bg-muted/50 transition-colors">
                    <RadioGroupItem value="no" id={`${question.id}-no`} />
                    <Label htmlFor={`${question.id}-no`} className="cursor-pointer">Tidak</Label>
                  </div>
                </RadioGroup>
              )}

              {question.type === 'scale' && (
                <div className="px-2 pt-2">
                  <Slider
                    value={[responses[question.id] || 5]}
                    onValueChange={([value]) => updateResponse(question.id, value)}
                    min={1}
                    max={10}
                    step={1}
                    className="mt-4"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground mt-3">
                    <span>1</span>
                    <span className="font-medium text-foreground text-sm">
                      {responses[question.id] || 5}
                    </span>
                    <span>10</span>
                  </div>
                </div>
              )}

              {question.type === 'text' && (
                <Textarea
                  placeholder="Tulis jawabanmu..."
                  value={responses[question.id] || ''}
                  onChange={(e) => updateResponse(question.id, e.target.value)}
                  rows={3}
                  className="resize-none rounded-xl"
                />
              )}
            </motion.div>
          ))}
        </div>
      );
    }

    // Peer Observation Quest
    if (questType === 'observation' && content?.peerObservation) {
      const { behaviorToObserve, checklistItems, photoRequired } = content.peerObservation;

      return (
        <div className="space-y-4">
          {/* Observee Selection */}
          <div className="p-4 rounded-2xl bg-primary/5 border border-primary/10">
            <div className="flex items-center gap-2 mb-3">
              <UserCheck className="h-5 w-5 text-primary" />
              <h3 className="font-semibold">Pilih Rekan yang Diobservasi</h3>
            </div>
            <Select value={selectedObservee} onValueChange={setSelectedObservee}>
              <SelectTrigger className="rounded-xl">
                <SelectValue placeholder="Pilih rekan kerja..." />
              </SelectTrigger>
              <SelectContent>
                {teamMembers.map((member) => (
                  <SelectItem key={member.id} value={member.id}>
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-xs">
                        {member.display_name?.charAt(0) || '?'}
                      </div>
                      {member.display_name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedObservee && (
              <motion.div 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-2 flex items-center gap-2 text-sm text-green-600"
              >
                <CheckCircle className="h-4 w-4" />
                <span>Rekan terpilih</span>
              </motion.div>
            )}
          </div>

          <div className="p-4 rounded-2xl bg-card/50">
            <div className="flex items-center gap-2 mb-3">
              <Eye className="h-5 w-5 text-primary" />
              <h3 className="font-semibold">Perilaku yang Diamati</h3>
            </div>
            <p className="text-muted-foreground text-sm">{behaviorToObserve}</p>
          </div>

          <div className="p-4 rounded-2xl bg-card/50">
            <h3 className="font-semibold mb-4">Checklist Observasi</h3>
            <div className="space-y-3">
              {checklistItems?.map((item: string, index: number) => (
                <div key={index} className="flex items-center space-x-3 p-3 rounded-xl hover:bg-muted/30 transition-colors">
                  <Checkbox
                    id={`check-${index}`}
                    checked={checkedItems[`item-${index}`] || false}
                    onCheckedChange={() => toggleCheckItem(`item-${index}`)}
                  />
                  <Label htmlFor={`check-${index}`} className="text-sm cursor-pointer">
                    {item}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          <div className="p-4 rounded-2xl bg-card/50">
            <h3 className="font-semibold mb-3">Catatan Observasi</h3>
            <Textarea
              placeholder="Tambahkan catatan observasimu..."
              value={observationNotes}
              onChange={(e) => setObservationNotes(e.target.value)}
              rows={3}
              className="resize-none rounded-xl"
            />
          </div>

          {photoRequired && (
            <div className="p-4 rounded-2xl bg-card/50">
              <h3 className="font-semibold mb-3">Foto Bukti</h3>
              {observationPhoto ? (
                <div className="relative">
                  <img 
                    src={observationPhoto} 
                    alt="Observation" 
                    className="w-full h-48 object-cover rounded-xl"
                  />
                  <Button 
                    variant="secondary" 
                    size="sm" 
                    className="absolute bottom-2 right-2 rounded-lg"
                    onClick={handleTakePhoto}
                  >
                    Ambil Ulang
                  </Button>
                </div>
              ) : (
                <Button 
                  variant="outline" 
                  className="w-full h-28 flex flex-col gap-2 rounded-xl border-dashed"
                  onClick={handleTakePhoto}
                >
                  <Camera className="h-6 w-6" />
                  <span className="text-sm">Ambil Foto</span>
                </Button>
              )}
            </div>
          )}
        </div>
      );
    }

    // Record Safety Video Quest
    if (questType === 'recording' && content?.safetyMessage) {
      const { topic, promptQuestions, maxDuration } = content.safetyMessage;

      return (
        <div className="space-y-4">
          <div className="p-4 rounded-2xl bg-card/50">
            <div className="flex items-center gap-2 mb-3">
              <Video className="h-5 w-5 text-primary" />
              <h3 className="font-semibold">Topik Video</h3>
            </div>
            <p className="text-muted-foreground text-sm">{topic}</p>
          </div>

          {promptQuestions?.length > 0 && (
            <div className="p-4 rounded-2xl bg-card/50">
              <h3 className="font-semibold mb-3">Panduan Pertanyaan</h3>
              <ul className="space-y-2">
                {promptQuestions.map((q: string, index: number) => (
                  <li key={index} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <span className="text-primary font-medium">{index + 1}.</span>
                    {q}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="p-4 rounded-2xl bg-card/50">
            <h3 className="font-semibold mb-4">Rekam Video</h3>
            <div className="space-y-4">
              <div className="relative aspect-video bg-black rounded-xl overflow-hidden">
                {isRecording || videoStream ? (
                  <video
                    ref={previewRef}
                    autoPlay
                    muted
                    playsInline
                    className="w-full h-full object-cover"
                    style={{ transform: 'scaleX(-1)' }}
                  />
                ) : videoUrl ? (
                  <video
                    ref={videoRef}
                    src={videoUrl}
                    controls
                    playsInline
                    autoPlay={false}
                    className="w-full h-full object-cover"
                    onEnded={() => setIsPlaying(false)}
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-muted-foreground">
                    <div className="text-center">
                      <Video className="h-10 w-10 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Tekan untuk mulai merekam</p>
                    </div>
                  </div>
                )}

                {isRecording && (
                  <div className="absolute top-3 left-3 flex items-center gap-2 bg-black/50 px-2 py-1 rounded-lg">
                    <motion.div
                      className="w-3 h-3 bg-red-500 rounded-full"
                      animate={{ opacity: [1, 0.3, 1] }}
                      transition={{ duration: 1, repeat: Infinity }}
                    />
                    <span className="text-white text-sm font-mono">{formatTime(recordingTime)}</span>
                  </div>
                )}
              </div>

              <div className="text-center">
                <p className="text-xs text-muted-foreground mb-4">
                  Maksimal: {formatTime(maxDuration || 60)}
                </p>
                
                <Button
                  size="lg"
                  variant={isRecording ? "destructive" : "default"}
                  className="w-16 h-16 rounded-full"
                  onClick={toggleRecording}
                >
                  {isRecording ? (
                    <Square className="h-6 w-6" />
                  ) : (
                    <Video className="h-6 w-6" />
                  )}
                </Button>

                <p className="mt-4 text-sm text-muted-foreground">
                  {isRecording ? (
                    <span className="text-red-500 font-medium">Sedang merekam...</span>
                  ) : recordedBlob ? (
                    <span className="text-green-600">✓ Video tersimpan ({formatTime(recordingTime)})</span>
                  ) : (
                    'Tekan untuk mulai merekam'
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Team Challenge Quest
    if (questType === 'team_challenge' && content?.clusterChallenge) {
      const { goal, mode, durationDays, targetMetric, targetValue, pointsPerAction } = content.clusterChallenge;

      return (
        <div className="space-y-4">
          <div className="p-4 rounded-2xl bg-card/50">
            <div className="flex items-center gap-2 mb-3">
              <Trophy className="h-5 w-5 text-primary" />
              <h3 className="font-semibold">Target Challenge</h3>
            </div>
            <p className="text-muted-foreground text-sm">{goal}</p>
          </div>

          <div className="p-4 rounded-2xl bg-card/50">
            <div className="grid grid-cols-2 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-primary">{targetValue}</div>
                <div className="text-xs text-muted-foreground">{targetMetric?.replace('_', ' ')}</div>
              </div>
              <div>
                <div className="text-2xl font-bold">{durationDays}</div>
                <div className="text-xs text-muted-foreground">Hari</div>
              </div>
            </div>
          </div>

          <div className="p-4 rounded-2xl bg-card/50 flex items-center justify-between">
            <span className="text-sm">Mode</span>
            <Badge variant="secondary" className="bg-muted/50">
              {mode === 'team_vs_team' ? 'Tim vs Tim' : 'Bersama-sama'}
            </Badge>
          </div>

          {quest.cluster_quests && (
            <div className="p-4 rounded-2xl bg-card/50">
              <div className="flex items-center gap-2 mb-3">
                <Users className="h-4 w-4 text-primary" />
                <span className="font-medium">Progress Tim</span>
              </div>
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-muted-foreground">Target: {quest.cluster_quests.target_value}</span>
                <span className="font-medium">{Math.round(quest.cluster_quests.current_value)}</span>
              </div>
              <Progress value={(quest.cluster_quests.current_value / quest.cluster_quests.target_value) * 100} className="h-2" />
            </div>
          )}

          <div className="p-4 rounded-2xl bg-primary/5 border border-primary/10">
            <h3 className="font-semibold mb-3">Kontribusimu</h3>
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-muted-foreground">Total kontribusi</span>
              <span className="text-2xl font-bold">{contributionCount}</span>
            </div>
            <Button onClick={handleLogContribution} className="w-full rounded-xl">
              <Target className="h-4 w-4 mr-2" />
              Log Kontribusi (+{pointsPerAction} poin)
            </Button>
          </div>
        </div>
      );
    }

    // Challenge Quest
    if (questType === 'challenge' && content?.challenge) {
      const { goal, targetValue, durationDays } = content.challenge;
      const last7Days = getLast7Days();

      return (
        <div className="space-y-4">
          <div className="p-4 rounded-2xl bg-card/50">
            <div className="flex items-center gap-2 mb-3">
              <Target className="h-5 w-5 text-primary" />
              <h3 className="font-semibold">Challenge Goal</h3>
            </div>
            <p className="text-muted-foreground text-sm">{goal}</p>
          </div>

          <div className="p-4 rounded-2xl bg-card/50">
            <div className="grid grid-cols-2 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-primary">{targetValue}</div>
                <div className="text-xs text-muted-foreground">Target Hari</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-green-600">{dailyCompletions.length}</div>
                <div className="text-xs text-muted-foreground">Hari Tercapai</div>
              </div>
            </div>
            <Progress 
              value={(dailyCompletions.length / targetValue) * 100} 
              className="h-2 mt-4" 
            />
          </div>

          <div className="p-4 rounded-2xl bg-card/50">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Progress 7 Hari Terakhir
            </h3>
            <div className="grid grid-cols-7 gap-2">
              {last7Days.map((date) => {
                const isCompleted = dailyCompletions.includes(date);
                const isToday = date === new Date().toISOString().split('T')[0];
                return (
                  <div 
                    key={date}
                    className={`aspect-square rounded-lg flex items-center justify-center text-xs font-medium ${
                      isCompleted 
                        ? 'bg-green-500 text-white' 
                        : isToday 
                          ? 'bg-primary/20 border-2 border-primary' 
                          : 'bg-muted/50'
                    }`}
                  >
                    {isCompleted ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      new Date(date).getDate()
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="p-4 rounded-2xl bg-primary/5 border border-primary/10">
            {todayCompleted ? (
              <div className="text-center py-4">
                <CheckCircle className="h-10 w-10 text-green-500 mx-auto mb-2" />
                <p className="font-semibold text-green-600">Hari ini sudah check-in!</p>
                <p className="text-sm text-muted-foreground">Kembali besok untuk lanjutkan streak</p>
              </div>
            ) : (
              <>
                <h3 className="font-semibold mb-2">Check-in Hari Ini</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Tandai bahwa kamu sudah menjalankan challenge hari ini
                </p>
                <Button onClick={handleDailyCheckIn} className="w-full rounded-xl">
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Tandai Hari Ini Selesai
                </Button>
              </>
            )}
          </div>
        </div>
      );
    }

    // Commitment Quest
    if (questType === 'commitment') {
      const commitmentData = content?.commitment || {};
      const statement = commitmentData.statement || quest.quest_templates?.description || 'Saya berkomitmen untuk menjaga keselamatan kerja.';

      return (
        <div className="space-y-4">
          <div className="p-6 rounded-2xl bg-gradient-to-br from-primary/5 to-primary/10 border border-primary/10">
            <div className="text-center mb-6">
              <div className="w-14 h-14 rounded-2xl bg-primary/20 flex items-center justify-center mx-auto mb-4">
                <Target className="h-7 w-7 text-primary" />
              </div>
              <h3 className="text-lg font-semibold">Pernyataan Komitmen</h3>
            </div>
            
            <div className="bg-background/80 rounded-xl p-4 border border-border/50">
              <p className="text-foreground leading-relaxed italic text-center">
                "{statement}"
              </p>
            </div>

            {commitmentData.duration && (
              <p className="text-center text-sm text-muted-foreground mt-4">
                Periode: {commitmentData.duration === 'daily' ? 'Harian' : 
                         commitmentData.duration === 'weekly' ? 'Mingguan' : 
                         commitmentData.duration === 'one_time' ? 'Satu Kali' : commitmentData.duration}
              </p>
            )}
          </div>

          <div className="p-4 rounded-2xl bg-card/50">
            <div className="flex items-start gap-3">
              <Checkbox
                id="commitment-agree"
                checked={commitmentAgreed}
                onCheckedChange={(checked) => setCommitmentAgreed(checked === true)}
                className="mt-1"
              />
              <Label htmlFor="commitment-agree" className="text-sm leading-relaxed cursor-pointer">
                Saya memahami dan berkomitmen untuk menjalankan pernyataan di atas dengan sungguh-sungguh.
              </Label>
            </div>
          </div>

          {commitmentAgreed && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center justify-center gap-2 text-green-600"
            >
              <CheckCircle className="h-5 w-5" />
              <span className="font-medium">Komitmen siap disubmit</span>
            </motion.div>
          )}
        </div>
      );
    }

    // Micro-Learning Quest (inline slides + learning points)
    if (questType === 'learning') {
      const slides = content?.slides || [];
      const learningPoints = content?.learning_points || [];
      
      return (
        <div className="space-y-4">
          {/* Learning Points */}
          {learningPoints.length > 0 && (
            <div className="p-4 rounded-2xl bg-primary/5 border border-primary/10">
              <div className="flex items-center gap-2 mb-3">
                <BookOpen className="h-5 w-5 text-primary" />
                <h3 className="font-semibold">Poin Pembelajaran</h3>
              </div>
              <ul className="space-y-2">
                {learningPoints.map((point: string, idx: number) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <span className="w-5 h-5 rounded-full bg-primary/20 text-primary text-xs flex items-center justify-center shrink-0 mt-0.5">
                      {idx + 1}
                    </span>
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Slides */}
          {slides.length > 0 && (
            <div className="space-y-3">
              {slides.map((slide: { title?: string; content?: string }, idx: number) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  className="p-4 rounded-2xl bg-card/50 border border-border/30"
                >
                  {slide.title && (
                    <h4 className="font-medium text-foreground mb-2">{slide.title}</h4>
                  )}
                  {slide.content && (
                    <p className="text-sm text-muted-foreground leading-relaxed">{slide.content}</p>
                  )}
                </motion.div>
              ))}
            </div>
          )}

          {/* Fallback if no slides/learning points */}
          {slides.length === 0 && learningPoints.length === 0 && (
            <div className="p-6 rounded-2xl bg-card/50 text-center">
              <BookOpen className="h-12 w-12 text-primary mx-auto mb-3" />
              <p className="text-muted-foreground">
                {quest.quest_templates?.description || 'Baca dan pahami materi pembelajaran ini.'}
              </p>
            </div>
          )}

          <div className="p-4 rounded-2xl bg-green-500/10 border border-green-500/20 text-center">
            <CheckCircle className="h-8 w-8 text-green-600 mx-auto mb-2" />
            <p className="text-sm text-green-700 dark:text-green-400">
              Tekan tombol di bawah setelah memahami materi
            </p>
          </div>
        </div>
      );
    }

    // Default
    return (
      <div className="p-8 rounded-2xl bg-card/50 text-center">
        <CheckCircle className="h-14 w-14 text-primary mx-auto mb-4" />
        <p className="text-muted-foreground">
          Tekan tombol di bawah untuk menyelesaikan quest ini
        </p>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Minimalist Header */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-xl border-b border-border/50 pt-safe">
        <div className="max-w-2xl mx-auto flex items-center gap-3 px-4 py-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/quests')} className="rounded-full h-9 w-9">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="font-semibold truncate">{quest.quest_templates?.title}</h1>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Star className="h-3.5 w-3.5 text-amber-500" />
              <span>{quest.quest_templates?.xp_reward} XP</span>
              {quest.expires_at && (
                <>
                  <span className="text-muted-foreground/30">•</span>
                  <Clock className="h-3.5 w-3.5" />
                  <span>{new Date(quest.expires_at).toLocaleDateString('id-ID')}</span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-4 space-y-4">
        {/* Description */}
        <div className="p-4 rounded-2xl bg-card/50">
          <p className="text-muted-foreground text-sm leading-relaxed">{quest.quest_templates?.description}</p>
          
          {quest.cluster_quests && questType !== 'team_challenge' && (
            <div className="mt-4 pt-4 border-t border-border/50">
              <div className="flex items-center gap-2 mb-2">
                <Users className="h-4 w-4 text-primary" />
                <span className="font-medium text-sm">Progress Tim</span>
              </div>
              <div className="flex items-center justify-between text-xs mb-1.5">
                <span className="text-muted-foreground">Target: {quest.cluster_quests.target_value}%</span>
                <span className="font-medium">{Math.round(quest.cluster_quests.current_value)}%</span>
              </div>
              <Progress value={quest.cluster_quests.current_value} className="h-1.5" />
            </div>
          )}
        </div>

        {renderQuestContent()}
      </div>

      {/* Submit Button */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-background/95 backdrop-blur-xl border-t border-border/50">
        <div className="max-w-2xl mx-auto">
          <Button 
            onClick={handleSubmit} 
            disabled={submitting}
            className="w-full h-12 rounded-xl text-base font-medium"
          >
            {submitting ? (
              'Mengirim...'
            ) : (
              <>
                Selesaikan Quest
                <Star className="h-5 w-5 ml-2" />
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
